import { Box, Typography } from "@mui/material";
import { Link, useParams } from "@tanstack/react-router";

import type { NavGroup, SubLink } from "./navConfig";
import type { ToOptions, LinkProps } from "@tanstack/react-router";

import { appContentGutter } from "@/design-tokens/layout";
import { materialColorsOpacity } from "@/design-tokens/material-colors";
import { shape } from "@/design-tokens/shape";
import { typography } from "@/design-tokens/typography";

interface SubNavProps {
  group: NavGroup;
  activeSubLink: SubLink | null;
}

export function SubNav({ group, activeSubLink }: SubNavProps) {
  const routeParams = useParams({ strict: false });
  const siteId = routeParams?.siteId ?? "";

  if (!group.subLinks || group.subLinks.length === 0) {
    return null;
  }

  return (
    <Box
      component="nav"
      aria-label="Sub navigation"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        px: appContentGutter,
        py: 3,
        backgroundColor: "common.white",
      }}
    >
      {group.subLinks.map((subLink) => {
        const isActive = activeSubLink?.label === subLink.label;
        const isDisabled = subLink.disabled === true;

        const params: Record<string, string> = { siteId };
        if (subLink.params && typeof subLink.params === "object") {
          Object.assign(params, subLink.params as Record<string, string>);
        }

        if (isDisabled) {
          return (
            <Box
              key={subLink.label}
              sx={{
                px: 6,
                py: 4,
                borderRadius: `${shape.borderRadius.md}px`,
                cursor: "default",
              }}
              aria-disabled="true"
            >
              <Typography
                sx={{
                  fontSize: typography.fontSize["0.8125rem"],
                  fontWeight: typography.fontWeight.medium,
                  color: "text.disabled",
                  whiteSpace: "nowrap",
                }}
              >
                {subLink.label}
              </Typography>
            </Box>
          );
        }

        return (
          <Box
            key={subLink.label}
            component={Link}
            to={subLink.route.to as ToOptions["to"]}
            params={params as LinkProps["params"]}
            sx={{
              px: 5,
              py: 3,
              borderRadius: `${shape.borderRadius.md}px`,
              textDecoration: "none",
              cursor: "pointer",
              backgroundColor: isActive
                ? "background.paper-elevation-1"
                : "transparent",
              "&:hover": isActive
                ? {}
                : { backgroundColor: materialColorsOpacity.violet["400-4%"] },
              transition: "background-color 0.15s",
            }}
          >
            <Typography
              sx={{
                fontSize: typography.fontSize["0.8125rem"],
                fontWeight: isActive
                  ? typography.fontWeight.semiBold
                  : typography.fontWeight.medium,
                color: isActive ? "primary.main" : "text.secondary",
                whiteSpace: "nowrap",
              }}
            >
              {subLink.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
